
import { useState } from "react";
import Layout from "../components/Layout";
import { Instagram, Twitter, Linkedin, Mail, Send } from "lucide-react";
import TikTokIcon from "../components/icons/TikTokIcon";
import { useToast } from "../hooks/use-toast";

interface FormData {
  name: string;
  email: string;
  message: string;
}

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create form data for submission
      const formSubmission = new FormData();
      formSubmission.append('name', formData.name);
      formSubmission.append('email', formData.email);
      formSubmission.append('message', formData.message);
      formSubmission.append('recipient', 'contact@yurilobo.com');

      // Use formspree.io as a simple email service
      const response = await fetch('https://formspree.io/f/xkgjjwlk', {
        method: 'POST',
        body: formSubmission,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        toast({
          title: "Message sent!",
          description: "Thank you for your message. I'll get back to you soon."
        });
        setFormData({
          name: "",
          email: "",
          message: ""
        });
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Something went wrong');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error sending message",
        description: "Please try again later or contact me directly at contact@yurilobo.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return <Layout>
      <section className="max-w-3xl mx-auto">
        <h1 className="text-3xl font-medium text-center mb-10">Contact</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-xl font-medium mb-4 flex items-center">
              <Mail className="mr-2 h-5 w-5" />
              Get in Touch
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  Name
                </label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-2 rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" placeholder="Your name" />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">
                  Email
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" placeholder="your.email@example.com" />
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1">
                  Message
                </label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-2 rounded-md border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all resize-none" placeholder="Tell me about your project..." />
              </div>
              
              <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-70">
                {isSubmitting ? <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span> : <span className="flex items-center">
                    Send Message
                    <Send className="ml-2 h-4 w-4" />
                  </span>}
              </button>
            </form>
          </div>
          
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-medium mb-6">Let's get social</h2>
              
              <div className="space-y-4">
                <a href="https://instagram.com/yurilobo" target="_blank" rel="noopener noreferrer" className="flex items-center p-3 rounded-md hover:bg-secondary transition-colors">
                  <Instagram className="h-5 w-5 mr-3" />
                  <span>@yurilobo</span>
                </a>
                
                <a href="https://tiktok.com/@yurilobo" target="_blank" rel="noopener noreferrer" className="flex items-center p-3 rounded-md hover:bg-secondary transition-colors">
                  <TikTokIcon className="h-5 w-5 mr-3" />
                  <span>@yurilobo</span>
                </a>
                
                <a href="https://twitter.com/yurilobo" target="_blank" rel="noopener noreferrer" className="flex items-center p-3 rounded-md hover:bg-secondary transition-colors">
                  <Twitter className="h-5 w-5 mr-3" />
                  <span>@yurilobo</span>
                </a>
                
                <a href="https://linkedin.com/in/yurilobo" target="_blank" rel="noopener noreferrer" className="flex items-center p-3 rounded-md hover:bg-secondary transition-colors">
                  <Linkedin className="h-5 w-5 mr-3" />
                  <span>Yuri Lobo</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
};

export default Contact;
